import { Fragment, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { getFormFieldComponent } from "@/react/components/SettingsApp/Emails/components/Builder/BuilderFields/ToForm/getFormFieldComponent";
import { geoLocationFilter } from "@/react/components/SettingsApp/ManageMembers/TabContent/Filters/filterConfiguration";
import { useFilterValueContext } from "@circle-react-shared/MemberFilters/FilterValueContext";
import { getIsValueEmpty } from "@circle-react-shared/MemberFilters/getIsValueEmpty";
import type {
  BaseFilter,
  RenderFilter,
  RenderProfileFieldFilter,
} from "@circle-react-shared/MemberFilters/types";
import { isProfileFieldBaseFilter } from "@circle-react-shared/MemberFilters/types";

interface DisplayedFiltersProps {
  displayedFilters: BaseFilter[];
  onRemoveFilter: (id: string) => void;
  platformFilters: RenderFilter[];
  profileFieldFilters: RenderProfileFieldFilter[];
}

export function DisplayedFilters({
  displayedFilters,
  onRemoveFilter,
  platformFilters,
  profileFieldFilters,
}: Readonly<DisplayedFiltersProps>) {
  const { filterParams, onApply } = useFilterValueContext();

  // We're using the form context to know whether we're being rendered inside a form or not
  // so we can wrap the component in the getFormFieldComponent HoC
  const formContext = useFormContext();

  // We need to include the geolocation filter in the list of platform filters
  // because on the url, the filter is called geo_location and it will be always
  // presented as platform filter. Thats why when we render the filters, we need to
  // include it in the list of platform filters. But we cannot actually add this to the passed
  // platformFilters array because then we will have two location filters on the page.
  const allPlatformFieldsPlusGeolocation = useMemo(
    () => [
      ...platformFilters,
      {
        ...geoLocationFilter,
        component: formContext
          ? getFormFieldComponent(geoLocationFilter.component)
          : geoLocationFilter.component,
      },
    ],
    [platformFilters, formContext],
  );

  const platformFilterMap = useMemo(
    () =>
      allPlatformFieldsPlusGeolocation.reduce((acc, filter) => {
        acc.set(filter.name, filter);
        return acc;
      }, new Map<string, RenderFilter>()),
    [allPlatformFieldsPlusGeolocation],
  );

  const profileFieldFilterMap = useMemo(
    () =>
      profileFieldFilters.reduce((acc, filter) => {
        acc.set(filter.id, filter);
        return acc;
      }, new Map<number, RenderProfileFieldFilter>()),
    [profileFieldFilters],
  );
  const paramValueMap = useMemo(
    () =>
      filterParams.reduce((acc, param) => {
        acc.set(param.id, param);
        return acc;
      }, new Map<string, BaseFilter>()),
    [filterParams],
  );

  return (
    <Fragment>
      {displayedFilters.map((displayedFilter, index) => {
        const { key, id } = displayedFilter;
        let renderFilter = platformFilterMap.get(key);

        if (isProfileFieldBaseFilter(displayedFilter)) {
          renderFilter = profileFieldFilterMap.get(
            Number(displayedFilter.profile_field_id),
          );
        }
        if (!renderFilter) return null;
        const { name, component: Component, ...props } = renderFilter;
        const paramValue = paramValueMap.get(id);
        let isTemplateDefaultOpen = false;
        /** When a workflow is created from a template, it will have value set to "" for initial open. */
        if (paramValue?.value === "") {
          isTemplateDefaultOpen = true;
        }

        return (
          <Component
            key={id}
            name={name}
            index={index}
            param={{ ...displayedFilter, ...paramValue }}
            onApply={(param: BaseFilter) => {
              if (getIsValueEmpty(param)) {
                onRemoveFilter(id);
              }
              onApply?.(id, param);
            }}
            isInitialOpen={
              isTemplateDefaultOpen || displayedFilter.isInitialOpen
            }
            shouldShowRemoveButton
            {...props}
          />
        );
      })}
    </Fragment>
  );
}
